import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Stack,
  alpha,
} from '@mui/material';
import {
  Lock as LockIcon,
  Star as StarIcon,
  RocketLaunch as RocketIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

interface SubscriptionGateProps {
  feature: string;
  description?: string;
  children?: React.ReactNode;
}

export const SubscriptionGate: React.FC<SubscriptionGateProps> = ({ 
  feature, 
  description,
  children 
}) => {
  const navigate = useNavigate();
  const { hasActiveSubscription } = useAuth();

  if (hasActiveSubscription) {
    return <>{children}</>;
  }

  // Show upgrade prompt
  return (
    <Box sx={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      minHeight: '60vh',
      p: 3 
    }}>
      <Card sx={{ 
        maxWidth: 600, 
        width: '100%',
        background: 'linear-gradient(135deg, #f5f5f5 0%, #fafafa 100%)',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 3,
        overflow: 'hidden'
      }}>
        <Box sx={{ 
          background: 'linear-gradient(135deg, #9c27b0 0%, #7b1fa2 100%)',
          color: 'white',
          p: 3,
          textAlign: 'center'
        }}>
          <LockIcon sx={{ fontSize: 48, mb: 2 }} />
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
            Premium Feature
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.9 }}>
            {feature}
          </Typography>
        </Box>
        
        <CardContent sx={{ p: 4 }}>
          <Typography variant="body1" color="textSecondary" sx={{ mb: 4, textAlign: 'center' }}>
            {description || `Upgrade to a premium plan to unlock ${feature} and many other powerful features.`}
          </Typography>

          <Stack spacing={2} sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ 
                width: 40, 
                height: 40, 
                borderRadius: '50%',
                bgcolor: alpha('#9c27b0', 0.1),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <StarIcon sx={{ color: '#9c27b0' }} />
              </Box>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Unlimited AI Functions
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Create as many chatbots and tools as you need
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ 
                width: 40, 
                height: 40, 
                borderRadius: '50%',
                bgcolor: alpha('#9c27b0', 0.1),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <RocketIcon sx={{ color: '#9c27b0' }} />
              </Box>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Advanced Features
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Custom branding, embeds, personal tools, and more
                </Typography>
              </Box>
            </Box>
          </Stack>

          <Button
            variant="contained"
            fullWidth
            size="large"
            onClick={() => navigate('/dashboard/subscription')}
            sx={{
              py: 1.5,
              background: 'linear-gradient(135deg, #9c27b0 0%, #7b1fa2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #8e24aa 0%, #6a1b9a 100%)',
              }
            }}
          >
            Upgrade Now
          </Button>

          <Typography variant="caption" color="textSecondary" sx={{ 
            display: 'block', 
            textAlign: 'center',
            mt: 2 
          }}>
            Start with a 14-day free trial • Cancel anytime
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};

export default SubscriptionGate;
